import {useState} from 'react'
import {css} from '@emotion/react'

import {ProductMetadataResponse} from '../api/fetchProductMetadata'
import unitsToReadableString from '../utils/unitsToReadableString'
import {PricingResponse} from '../api/usePricing'
import useUsage from '../api/useUsage'

import SpinnerIcon from './SpinnerIcon'
import UsageTableSummary from './UsageTableSummary'
import PaginationControls from './PaginationControls'

const TIME_FORMAT = Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
})

const DATE_FORMAT = Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'numeric',
})

function toHumanTime(isoTime: string): string {
  const date = new Date(Date.parse(isoTime))
  return `${DATE_FORMAT.format(date)} at ${TIME_FORMAT.format(date)}`
}

const tableCss = css`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;

  thead {
    text-align: left;
  }

  tbody {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    overflow-y: auto;
  }

  tr {
    height: 40px;
    border: 0px solid transparent;
    outline: none;
  }

  td,
  th {
    padding: 0 12px;
  }

  tr:not(:first-of-type):nth-of-type(2n) {
    background: rgba(0, 0, 0, 0.04);
  }
`

const lastRowEntryCss = css`
  text-align: right;
  padding-right: 30px;
`

const loadingCss = css`
  text-align: center;
`

interface UsageTableProps {
  productMeta: ProductMetadataResponse
  price?: PricingResponse
}

function getFreshDate(dayNumber: number): Date {
  const date = new Date()
  date.setDate(dayNumber)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  return date
}

function getMonthSpan(lookback = 0): {startTimestamp: string; endTimestamp: string} {
  // billing cycles are 25th to the 25th
  const startDate = getFreshDate(25)
  startDate.setMonth(startDate.getMonth() - 1 - lookback)
  const endDate = getFreshDate(25)
  endDate.setMonth(endDate.getMonth() - lookback)

  return {
    startTimestamp: startDate.toISOString(),
    endTimestamp: endDate.toISOString(),
  }
}

export default function UsageTable({productMeta, price}: UsageTableProps): JSX.Element {
  const [page, setPage] = useState<number>(1)
  const [usageDateRange] = useState(getMonthSpan(0)) // useState here to prepare to eventually allow selecting billing windows
  const [pageSize] = useState(15) // useState to allow for increasing/decreasing the page size
  const usageRecordsState = useUsage({
    page,
    stripePriceId: price?.stripePriceId,
    pageSize,
    startTimestamp: usageDateRange.startTimestamp,
    endTimestamp: usageDateRange.endTimestamp,
    charged: true,
  })

  if (usageRecordsState.status === 'ready' && usageRecordsState.value.count === 0) {
    return (
      <UsageTableSummary usageDateRange={usageDateRange} price={price} productMeta={productMeta} />
    )
  }

  return (
    <>
      <UsageTableSummary usageDateRange={usageDateRange} price={price} productMeta={productMeta} />
      <table css={tableCss}>
        <thead>
          <tr>
            <th>Date & Time</th>
            <th>Description</th>
            <th>{productMeta.unitUsageDescription}</th>
            <th align="right">{productMeta.usageExternalLinkTitle}</th>
          </tr>
        </thead>
        <tbody>
          {usageRecordsState.status === 'ready' &&
            usageRecordsState.value.results.map((row) => (
              <tr key={row.usageRecordId}>
                <td>{toHumanTime(row.timestamp)}</td>
                <td>{row.description}</td>
                <td>{unitsToReadableString(row.units, productMeta.unitType)}</td>
                {/* TODO: make this an external link */}
                <td css={lastRowEntryCss}>{row.requestUuid}</td>
              </tr>
            ))}
          {usageRecordsState.status === 'pending' && (
            <tr>
              <td colSpan={4} css={loadingCss}>
                <SpinnerIcon />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {usageRecordsState.status === 'ready' && (
        <PaginationControls
          page={page}
          pageSize={pageSize}
          count={usageRecordsState.value.count}
          setPage={setPage}
        />
      )}
    </>
  )
}
